// File: frontend/src/pages/InvestorPitch/components/slides/InsuranceCaseStudySlide/InsuranceCaseStudySlide.js

import React from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line, Cell
} from 'recharts';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ANIMATION_VARIANTS } from '../../../constants/animations';
import styles from './InsuranceCaseStudySlide.module.css';

const CardComponent = ({ title, children }) => {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      <div className={styles.cardContent}>
        {children}
      </div>
    </div>
  );
};

const InsuranceCaseStudySlide = () => {
  const { t } = useLanguage();

  // Portfolio composition data
  const portfolioData = [
    {
      name: "Auto",
      value: 45,
      color: "var(--color-pink-710)",
      premium: 1.44
    },
    {
      name: "Home",
      value: 40,
      color: "var(--color-pink-510)",
      premium: 1.28
    },
    {
      name: "Commercial",
      value: 15,
      color: "var(--color-pink-310)",
      premium: 0.48
    }
  ];

  // Expense ratio comparison data
  const expenseRatioData = [
    {
      name: "Traditional Insurer",
      value: 33.6,
      color: "var(--color-red-310)"
    },
    {
      name: "Allegory Platform",
      value: 6.6,
      color: "var(--color-green-310)"
    }
  ];

  // Waterfall chart data
  const totalExpenseAmount = 1074e6;

  // Calculate running total for the final value
  const calculateRunningTotal = (data) => {
    let total = 0;
    return data.map(item => {
      if (item.type === 'total') {
        total = item.value;
        return { ...item, running: total };
      } else if (item.type === 'reduction') {
        total += item.value;
        return { ...item, running: total };
      } else {
        return { ...item, value: total, running: total };
      }
    });
  };

  const initialData = [
    { 
      name: 'Current\nExpenses', 
      value: totalExpenseAmount, 
      type: 'total',
      color: 'var(--color-red-310)'
    },
    { 
      name: 'Simple\nTasks', 
      value: -totalExpenseAmount * 0.30 * 0.999, 
      type: 'reduction',
      color: 'var(--color-blue-210)'
    },
    { 
      name: 'Medium\nTasks', 
      value: -totalExpenseAmount * 0.30 * 0.80, 
      type: 'reduction',
      color: 'var(--color-blue-310)'
    },
    { 
      name: 'Complex\nTasks', 
      value: -totalExpenseAmount * 0.25 * 0.75, 
      type: 'reduction',
      color: 'var(--color-blue-410)'
    },
    { 
      name: 'Strategic\nInitiatives', 
      value: -totalExpenseAmount * 0.15 * 0.50, 
      type: 'reduction',
      color: 'var(--color-blue-510)'
    },
    { 
      name: 'Future\nExpenses', 
      type: 'final',
      color: 'var(--color-success)'
    }
  ];

  const waterfallData = calculateRunningTotal(initialData);

  const formatCurrency = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 1e6) {
      return `$${(value / 1e6).toFixed(0)} M`;
    } else if (absValue >= 1e3) {
      return `$${(value / 1e3).toFixed(0)} K`;
    }
    return `$${value}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipTitle}>{label}</p>
          {payload.map((p) => (
            <div key={p.name} className={styles.tooltipRow}>
              <span>{p.name}:</span>
              <span style={{ color: p.color }}>{p.value}%</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const WaterfallTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipLabel}>{label.replace('\n', ' ')}</p>
          <p className={styles.tooltipValue}>
            {data.type === 'reduction' 
              ? `Reduction: ${formatCurrency(Math.abs(data.value))}`
              : `Total: ${formatCurrency(Math.abs(data.running))}`
            }
          </p>
          {data.type === 'reduction' && (
            <p className={styles.tooltipPercentage}>
              {`(${(Math.abs(data.value) / totalExpenseAmount * 100).toFixed(1)}% of original)`}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      className={styles.container}
      variants={ANIMATION_VARIANTS.stagger.container}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className={styles.header}
        variants={ANIMATION_VARIANTS.fadeIn}
      >
        <h2 className={styles.title}>
          {t('investor_pitch.insurance_case_study.title')}
        </h2>
        {/* <p className={styles.subtitle}>
          {t('investor_pitch.insurance_case_study.subtitle', 'Achieving 80% reduction in operational expenses through AI-powered automation')}
        </p> */}
      </motion.div>

      <motion.div 
        className={styles.keyInsights}
        variants={ANIMATION_VARIANTS.fadeIn}
      >
        <div className={styles.insightsHeader}>
          <h3 className={styles.insightsTitle}>
            {t('investor_pitch.insurance_case_study.key_insights', 'Key Insights')}
          </h3>
        </div>
        <ul className={styles.insightsList}>
          {/* <li>
            {t('investor_pitch.insurance_case_study.insight_1')}
          </li>
          <li>
            {t('investor_pitch.insurance_case_study.insight_2')}
          </li>
          <li>
            {t('investor_pitch.insurance_case_study.insight_3')}
          </li>
          <li>
            {t('investor_pitch.insurance_case_study.insight_4')}
          </li> */}
          <li>
            {t('investor_pitch.insurance_case_study.insight_5')}
          </li>
          <li>
            {t('investor_pitch.insurance_case_study.insight_6')}
          </li>
        </ul>
      </motion.div>

      <motion.div 
        className={styles.content}
        variants={ANIMATION_VARIANTS.stagger.container}
      >
        <div className={styles.row}>
          <motion.div 
            className={styles.cardWrapper}
            variants={ANIMATION_VARIANTS.fadeIn}
          >
            <CardComponent title={t('investor_pitch.insurance_case_study.portfolio_composition', 'Portfolio Composition')}>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={portfolioData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number" 
                      tickFormatter={(value) => `${value}%`}
                      tick={{ fontSize: 14, fill: 'var(--color-white)' }} 
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 14, fill: 'var(--color-white)' }} 
                    />
                    <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {portfolioData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className={styles.portfolioTotal}>
                  <span>{t('investor_pitch.insurance_case_study.total_premium', 'Total Premium:')}</span>
                  <span className={styles.premiumValue}>$3.2 B</span>
                </div>
              </div>
            </CardComponent>
          </motion.div>

          <motion.div 
            className={styles.cardWrapper}
            variants={ANIMATION_VARIANTS.fadeIn}
          >
            <CardComponent title={t('investor_pitch.insurance_case_study.expense_ratio', 'Expense Ratio Comparison')}>
              <div className={styles.chartContainer}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={expenseRatioData}
                    margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 14, fill: 'var(--color-white)' }} 
                    />
                    <YAxis 
                      tickFormatter={(value) => `${value}%`}
                      tick={{ fontSize: 14, fill: 'var(--color-white)' }}  
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {expenseRatioData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className={styles.savingsHighlight}>
                  <span>{t('investor_pitch.insurance_case_study.reduction', 'Reduction:')}</span>
                  <span className={styles.savingsValue}>80%</span>
                </div>
              </div>
            </CardComponent>
          </motion.div>
        </div>

        <motion.div 
          className={styles.wideCardWrapper}
          variants={ANIMATION_VARIANTS.fadeIn}
        >
          <CardComponent title={t('investor_pitch.insurance_case_study.cost_reduction', 'Cost Reduction Waterfall')}>
            <div className={styles.chartContainer}>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart
                  data={waterfallData}
                  margin={{ top: 20, right: 30, left: 40, bottom: 20 }}
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    vertical={false}
                    stroke="rgba(255, 255, 255, 0.1)"
                  />
                  <XAxis 
                    dataKey="name"
                    angle={0}
                    interval={0}
                    tick={{
                      fill: 'var(--color-gray-300)',
                      fontSize: 14,
                      fontFamily: 'var(--font-primary)'
                    }}
                    tickLine={false}
                    axisLine={{ stroke: 'var(--color-gray-600)' }}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.abs(value / 1000000).toFixed(0)}M`}
                    label={{ 
                      value: 'Cost Amount', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: {
                        fill: 'var(--color-gray-300)',
                        fontSize: 14,
                        fontFamily: 'var(--font-primary)'
                      }
                    }}
                    tick={{
                      fill: 'var(--color-gray-300)',
                      fontSize: 14,
                      fontFamily: 'var(--font-primary)'
                    }}
                    tickLine={false}
                    axisLine={{ stroke: 'var(--color-gray-600)' }}
                  />
                  <Tooltip content={<WaterfallTooltip />} />
                  <Bar 
                    dataKey="value" 
                    radius={[4, 4, 0, 0]}
                  >
                    {waterfallData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`}
                        fill={entry.color}
                      />
                    ))}
                  </Bar>
                  <Line
                    type="step"
                    dataKey="running"
                    stroke="var(--color-gray-300)"
                    strokeWidth={2}
                    dot={{
                      fill: 'var(--color-gray-300)',
                      r: 4
                    }}
                    activeDot={{
                      r: 6,
                      fill: 'var(--color-primary)'
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <div className={styles.savingsSummary}>
                <div className={styles.savingsItem}>
                  <span className={styles.savingsLabel}>{t('investor_pitch.insurance_case_study.total_savings', 'Total Savings:')}</span>
                  <span className={styles.savingsValue}>$862 M</span>
                </div>
              </div>
            </div>
          </CardComponent>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default InsuranceCaseStudySlide;